
import React from 'react';
import { Download, FileText, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { SymptomFilter } from './SymptomFilter';
import { DateRangeSelector } from './DateRangeSelector';
import type { DateRange } from 'react-day-picker';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

interface DashboardControlsProps {
  symptomData: any[];
  selectedSymptoms: string[];
  setSelectedSymptoms: React.Dispatch<React.SetStateAction<string[]>>;
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
}

export const DashboardControls: React.FC<DashboardControlsProps> = ({
  symptomData,
  selectedSymptoms,
  setSelectedSymptoms,
  dateRange,
  setDateRange
}) => {
  // Function to export data as CSV
  const exportAsCSV = () => {
    try {
      // Filter data based on selected symptoms and date range
      const filteredData = symptomData.filter(item => 
        item.symptoms.some((symptom: string) => selectedSymptoms.includes(symptom))
      );
      
      if (filteredData.length === 0) {
        toast.error('No data available to export');
        return;
      }
      
      // Create CSV header
      const headers = ['Date', 'Symptoms', 'Intensity', 'Notes'];
      
      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...filteredData.map(item => {
          const date = new Date(item.logged_at).toLocaleDateString();
          const symptoms = item.symptoms.join('; ');
          const intensity = item.intensity_level || 'N/A';
          const notes = item.notes ? `"${item.notes.replace(/"/g, '""')}"` : 'N/A';
          
          return [date, symptoms, intensity, notes].join(',');
        })
      ].join('\n');
      
      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Set up download attributes and trigger download
      link.setAttribute('href', url);
      link.setAttribute('download', `symptom-data-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Data exported successfully as CSV');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data. Please try again.');
    }
  };
  
  // Function to export data as PDF
  const exportAsPDF = () => {
    // For now, show a coming soon message
    toast.info('PDF export feature coming soon!');
  };
  
  return (
    <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <SymptomFilter 
          symptomData={symptomData}
          selectedSymptoms={selectedSymptoms}
          setSelectedSymptoms={setSelectedSymptoms}
        />
        
        <DateRangeSelector 
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      </div>
      
      <div className="flex gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-hormony-charcoal"
            >
              <Download size={18} className="mr-1" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportAsCSV}>
              <FileText size={16} className="mr-2" />
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportAsPDF}>
              <Share size={16} className="mr-2" />
              Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
